import AsyncStorage from '@react-native-async-storage/async-storage';
import Config from 'react-native-config';
import { Platform } from 'react-native';
import { authApiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { getInstalledAppVersion } from '../native/AppVersion';
import { isVersionBelow, parseVersion } from '../utils/appVersion';

export type AppUpdatePlatform = 'ANDROID' | 'IOS';

export type AppVersionPolicy = {
  platform: AppUpdatePlatform;
  forceUpdateEnabled: boolean;
  minimumVersion: string | null;
  latestVersion: string | null;
  storeUrl: string | null;
  updateTitle: string | null;
  updateMessage: string | null;
};

export type AppUpdatePrompt =
  | { status: 'none' }
  | {
      status: 'mandatory' | 'optional';
      installedVersion: string;
      minimumVersion: string | null;
      latestVersion: string | null;
      /** Version the user should update to (minimum for mandatory, latest for optional). */
      targetVersion: string;
      storeUrl: string;
      title: string | null;
      message: string | null;
    };

/** @deprecated Prefer AppUpdatePrompt; kept for existing mandatory-only callers/tests. */
export type MandatoryUpdateCheck =
  | { required: false }
  | {
      required: true;
      installedVersion: string;
      minimumVersion: string;
      latestVersion: string | null;
      storeUrl: string;
      title: string | null;
      message: string | null;
    };

type Envelope<T> = { success?: boolean; data?: T };

const CACHE_KEY_PREFIX = 'appUpdate.policy.';
const NONE: AppUpdatePrompt = { status: 'none' };
const NOT_REQUIRED: MandatoryUpdateCheck = { required: false };

export function currentUpdatePlatform(): AppUpdatePlatform | null {
  if (Platform.OS === 'android') return 'ANDROID';
  if (Platform.OS === 'ios') return 'IOS';
  return null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function httpsUrl(value: unknown): string | null {
  const url = text(value);
  return url && /^https:\/\/\S+$/i.test(url) ? url : null;
}

export function normalizeAppVersionPolicy(
  raw: unknown,
  platform: AppUpdatePlatform,
): AppVersionPolicy {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const minimumVersion = parseVersion(text(data.minimumVersion)) ? text(data.minimumVersion) : null;
  const latestVersion = parseVersion(text(data.latestVersion)) ? text(data.latestVersion) : null;
  return {
    platform,
    forceUpdateEnabled: data.forceUpdateEnabled === true,
    minimumVersion,
    latestVersion,
    storeUrl: httpsUrl(data.storeUrl),
    updateTitle: text(data.updateTitle),
    updateMessage: text(data.updateMessage),
  };
}

export async function fetchAppVersionPolicy(
  platform: AppUpdatePlatform,
  options: { installedVersion?: string | null; bundleId?: string | null } = {},
): Promise<AppVersionPolicy> {
  const timeout = Number(Config.APP_UPDATE_CHECK_TIMEOUT_MS) || undefined;
  const params: Record<string, string> = { platform };
  if (options.installedVersion) params.installedVersion = options.installedVersion;
  if (options.bundleId) params.bundleId = options.bundleId;
  const response = await authApiClient.get<Envelope<unknown>>(
    API_ENDPOINTS.APP_VERSION.POLICY,
    { params, ...(timeout ? { timeout } : {}) },
  );
  return normalizeAppVersionPolicy(response.data?.data, platform);
}

async function readCachedPolicy(platform: AppUpdatePlatform): Promise<AppVersionPolicy | null> {
  try {
    const stored = await AsyncStorage.getItem(`${CACHE_KEY_PREFIX}${platform}`);
    return stored ? normalizeAppVersionPolicy(JSON.parse(stored), platform) : null;
  } catch {
    return null;
  }
}

async function writeCachedPolicy(policy: AppVersionPolicy): Promise<void> {
  try {
    await AsyncStorage.setItem(`${CACHE_KEY_PREFIX}${policy.platform}`, JSON.stringify(policy));
  } catch {
    // Cache is best-effort; the live policy is still applied this session.
  }
}

/**
 * Compares the installed build to the backend policy (the store-published
 * versions). Mandatory when force is on and below minimum; otherwise optional
 * when below the published latestVersion (Play Store / App Store).
 */
export function evaluateAppUpdate(
  policy: AppVersionPolicy | null,
  installedVersion: string | null,
): AppUpdatePrompt {
  if (!policy?.storeUrl || !installedVersion) return NONE;

  if (
    policy.forceUpdateEnabled &&
    policy.minimumVersion &&
    isVersionBelow(installedVersion, policy.minimumVersion)
  ) {
    return {
      status: 'mandatory',
      installedVersion,
      minimumVersion: policy.minimumVersion,
      latestVersion: policy.latestVersion,
      targetVersion: policy.minimumVersion,
      storeUrl: policy.storeUrl,
      title: policy.updateTitle,
      message: policy.updateMessage,
    };
  }

  if (policy.latestVersion && isVersionBelow(installedVersion, policy.latestVersion)) {
    return {
      status: 'optional',
      installedVersion,
      minimumVersion: policy.minimumVersion,
      latestVersion: policy.latestVersion,
      targetVersion: policy.latestVersion,
      storeUrl: policy.storeUrl,
      title: policy.updateTitle,
      message: policy.updateMessage,
    };
  }

  return NONE;
}

export function evaluateMandatoryUpdate(
  policy: AppVersionPolicy | null,
  installedVersion: string | null,
): MandatoryUpdateCheck {
  const result = evaluateAppUpdate(policy, installedVersion);
  if (result.status !== 'mandatory') return NOT_REQUIRED;
  return {
    required: true,
    installedVersion: result.installedVersion,
    minimumVersion: result.minimumVersion as string,
    latestVersion: result.latestVersion,
    storeUrl: result.storeUrl,
    title: result.title,
    message: result.message,
  };
}

async function loadPolicy(
  platform: AppUpdatePlatform,
  installed: { versionName: string; bundleId: string | null },
): Promise<AppVersionPolicy | null> {
  try {
    const policy = await fetchAppVersionPolicy(platform, {
      installedVersion: installed.versionName,
      bundleId: installed.bundleId,
    });
    await writeCachedPolicy(policy);
    return policy;
  } catch {
    return readCachedPolicy(platform);
  }
}

/**
 * Checks whether the installed build should update.
 * Installed version comes from the native module; latestVersion and storeUrl
 * come from the backend, which resolves them live from the Play / App Store.
 */
export async function checkAppUpdate(): Promise<AppUpdatePrompt> {
  const platform = currentUpdatePlatform();
  const installed = getInstalledAppVersion();
  if (!platform || !installed) return NONE;
  const policy = await loadPolicy(platform, installed);
  return evaluateAppUpdate(policy, installed.versionName);
}

/**
 * Decides whether the installed build must be updated before use.
 * Offline launches keep enforcing the last cached minimum when force was on.
 */
export async function checkMandatoryUpdate(): Promise<MandatoryUpdateCheck> {
  const result = await checkAppUpdate();
  if (result.status !== 'mandatory') return NOT_REQUIRED;
  return {
    required: true,
    installedVersion: result.installedVersion,
    minimumVersion: result.minimumVersion as string,
    latestVersion: result.latestVersion,
    storeUrl: result.storeUrl,
    title: result.title,
    message: result.message,
  };
}
